import * as React from 'react';

import type {ReportRowData, ComparedActivityReportSummary} from 'nutshell-core/types';

import {ActivityTableQuotaBar} from './activity-table-quota-bar';
import {ActivityTableQuotaHeader} from './activity-table-quota-header';
import {ActivityTableQuotaVisualDelta} from './activity-table-quota-visual-delta';
import {NewActivityTableQuotaHeader} from './new-activity-table-quota-header';

import styles from './activity-report-summary-table.module.css';

type ActivityTableQuotaCellProps = {
    isTotalRow?: boolean; // TODO (@ianvs): enforce that `totals` must be provided if true,
    data: ComparedActivityReportSummary<Array<ReportRowData>>;
    totals?: ComparedActivityReportSummary<ReportRowData> | null | undefined;
    columnIndex: number;
    rowIndex?: number;
    style: any;
};

export const ActivityTableQuotaCell = ({
    style,
    data,
    totals,
    rowIndex,
    columnIndex,
    isTotalRow,
}: ActivityTableQuotaCellProps) => {
    if (isTotalRow && !totals) return null;

    let containerStyleName;
    let cellStyleName = '';
    if (isTotalRow) {
        containerStyleName = 'cell-totals';
        cellStyleName = 'quota-totals-cell';
    } else if (rowIndex) {
        containerStyleName = rowIndex % 2 === 0 ? 'cell-table-striped' : 'cell-table';
        cellStyleName = 'quota-cell';
    }
    const adjustedRowIndex = rowIndex ? rowIndex - 1 : 0; // Account for fixed header
    const adjustedColumnIndex = columnIndex - 1; // Account for fixed column

    const currentRowData =
        isTotalRow && totals ? totals.currentPeriod : data.currentPeriod[adjustedRowIndex];
    const previousRowData =
        isTotalRow && totals
            ? totals.compareTo
            : data.compareTo && data.compareTo[adjustedRowIndex];

    const currentCellData =
        currentRowData && currentRowData.segments && currentRowData.segments[adjustedColumnIndex]
            ? currentRowData.segments[adjustedColumnIndex].totalsByReport[0]
            : undefined;
    const previousCellData =
        previousRowData && previousRowData.segments && previousRowData.segments[adjustedColumnIndex]
            ? previousRowData.segments[adjustedColumnIndex].totalsByReport[0]
            : undefined;

    const currentQuota =
        currentCellData && currentCellData.benchmark ? currentCellData.benchmark.value : 0;
    const previousQuota =
        previousCellData && previousCellData.benchmark ? previousCellData.benchmark.value : 0;

    const currentStartTime = currentCellData ? currentCellData.startTime : undefined;
    const previousStartTime = currentCellData ? currentCellData.startTime : undefined;

    const currentEndTime = currentCellData ? currentCellData.endTime : undefined;
    const previousEndTime = currentCellData ? currentCellData.endTime : undefined;

    const currentTotal = currentCellData && currentCellData.count ? currentCellData.count.value : 0;
    const previousTotal =
        previousCellData && previousCellData.count ? previousCellData.count.value : 0;

    const currentPercent = currentQuota > 0 ? currentTotal / currentQuota : 0;
    const previousPercent = previousQuota > 0 ? previousTotal / previousQuota : 0;

    const hasCurrentCellData = typeof currentCellData !== 'undefined';
    const hasPreviousCellData = typeof previousCellData !== 'undefined';

    const currentCellClassName = NutClientConfig.hasActivityReportExpansionFF
        ? undefined
        : // @ts-expect-error
          styles[cellStyleName];
    const previousCellClassName = NutClientConfig.hasActivityReportExpansionFF
        ? undefined
        : // @ts-expect-error
          styles[`${cellStyleName}-previous`];

    const currentAverageDurationMinutes =
        currentCellData && currentCellData.averageDurationMinutes
            ? currentCellData.averageDurationMinutes.formatted
            : '-';
    const previousAverageDurationMinutes =
        previousCellData && previousCellData.averageDurationMinutes
            ? previousCellData.averageDurationMinutes.formatted
            : '-';

    let currentCellComponents;
    if (hasCurrentCellData) {
        if (NutClientConfig.hasActivityReportExpansionFF) {
            currentCellComponents = [
                <NewActivityTableQuotaHeader
                    key='current-header'
                    quota={currentQuota}
                    total={currentTotal}
                    averageDurationMinutes={currentAverageDurationMinutes}
                />,
            ];
        } else {
            currentCellComponents = [
                <ActivityTableQuotaHeader
                    key='current-header'
                    quota={currentQuota}
                    total={currentTotal}
                    percent={currentPercent}
                />,
                <ActivityTableQuotaBar
                    key='current-bar'
                    quota={currentQuota}
                    startTime={currentStartTime}
                    endTime={currentEndTime}
                    percent={currentPercent}
                />,
            ];
        }
    }
    let previousCellComponents;
    if (hasPreviousCellData) {
        if (NutClientConfig.hasActivityReportExpansionFF) {
            previousCellComponents = [
                <NewActivityTableQuotaHeader
                    key='previous-header'
                    quota={previousQuota}
                    total={previousTotal}
                    averageDurationMinutes={previousAverageDurationMinutes}
                />,
            ];
        } else {
            previousCellComponents = [
                <ActivityTableQuotaHeader
                    key='previous-header'
                    quota={previousQuota}
                    total={previousTotal}
                    percent={previousPercent}
                />,
                <ActivityTableQuotaBar
                    key='previous-bar'
                    quota={previousQuota}
                    startTime={previousStartTime}
                    endTime={previousEndTime}
                    percent={previousPercent}
                />,
            ];
        }
    }

    let visualDeltaComponent;
    if (hasPreviousCellData && hasCurrentCellData) {
        visualDeltaComponent = (
            <ActivityTableQuotaVisualDelta
                previousTotal={previousTotal}
                currentTotal={currentTotal}
            />
        );
    }

    return (
        // @ts-expect-error
        <div style={style} className={styles[containerStyleName]}>
            <div className='flex flex-dir-col justify-start'>{visualDeltaComponent}</div>
            <div className={styles['quota-container']}>
                {currentCellComponents ? (
                    <div className={currentCellClassName}>{currentCellComponents}</div>
                ) : undefined}
                {previousCellComponents ? (
                    <div className={previousCellClassName}>{previousCellComponents}</div>
                ) : undefined}
            </div>
        </div>
    );
};
